import type { RouteNamedMap } from '@/types/routes.generated.d'
import { computed, type ComputedRef } from 'vue'
import {
  useRoute,
  useRouter,
  type RouteLocationNormalizedLoaded,
} from 'vue-router'

/**
 * 引数で与えられたルート名と現在のルートが一致しているとき、route を返す
 *
 * @param name ルート名
 * @returns ルート名が存在する場合はそのルート名、存在しない場合はundefined
 */
export function useMaybeRoute<Name extends keyof RouteNamedMap>(
  name: Name
): ComputedRef<Maybe<RouteLocationNormalizedLoaded<Name>>> {
  const route = useRoute(name)
  const router = useRouter()

  const maybeRoute = computed(() => {
    if (router.currentRoute.value.name === name) {
      return route
    }

    return undefined
  })

  return maybeRoute
}

/**
 * 引数で与えられたルート名のうち、現在のルートと一致しているものを返す
 *
 * @param names ルート名
 * @returns ルート名が存在する場合はそのルート名、存在しない場合はundefined
 */
export function useMaybeRoutes<Name extends keyof RouteNamedMap>(
  names: Name[]
): ComputedRef<Maybe<RouteLocationNormalizedLoaded<Name>>> {
  const route = useRoute()
  const router = useRouter()

  const maybeRoute = computed<Maybe<RouteLocationNormalizedLoaded<Name>>>(
    () => {
      const currentRouteName = names.find(name => {
        return router.currentRoute.value.name === name
      })

      if (currentRouteName === undefined) {
        return undefined
      }

      return route as RouteLocationNormalizedLoaded<Name>
    }
  )

  return maybeRoute
}
